/**
 * Workflowy REST API client for Task Sidekick 2
 * Enables 2-way sync: pushes task updates back to Workflowy
 * Documentation: https://beta.workflowy.com/api-reference/
 */

const WORKFLOWY_API_BASE = 'https://beta.workflowy.com/api/v1'

interface WorkflowyCredentials {
  apiKey: string
}

/**
 * Get Workflowy credentials from Chrome storage
 */
async function getWorkflowyCredentials(): Promise<WorkflowyCredentials | null> {
  if (typeof chrome === 'undefined' || !chrome.storage) {
    return null
  }

  return new Promise((resolve) => {
    chrome.storage.local.get('workflowyApiSettings', (result) => {
      resolve(result.workflowyApiSettings || null)
    })
  })
}

/**
 * Make authenticated request to Workflowy API
 */
async function workflowyApiRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
  const creds = await getWorkflowyCredentials()
  if (!creds?.apiKey) {
    throw new Error('Workflowy API not configured')
  }

  const url = `${WORKFLOWY_API_BASE}${endpoint}`
  const response = await fetch(url, {
    ...options,
    headers: {
      'Authorization': `Bearer ${creds.apiKey}`,
      'Content-Type': 'application/json',
      ...options.headers
    }
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.message || error.error || `HTTP ${response.status}`)
  }

  // Handle empty responses (204)
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return { status: 'ok' }
  }

  return response.json()
}

/**
 * Get a specific node by ID
 */
async function getNode(nodeId: string): Promise<any> {
  return workflowyApiRequest(`/nodes/${nodeId}`)
}

/**
 * Update a node's content
 */
async function updateNode(nodeId: string, updates: { name?: string; note?: string; layoutMode?: string }): Promise<boolean> {
  try {
    await workflowyApiRequest(`/nodes/${nodeId}`, {
      method: 'POST',
      body: JSON.stringify(updates)
    })
    return true
  } catch (error) {
    console.error('[WorkflowyAPI] Failed to update node:', error)
    return false
  }
}

/**
 * Toggle task completion (complete/uncomplete)
 */
export async function toggleWorkflowyTaskComplete(taskId: string, completed: boolean): Promise<boolean> {
  try {
    const creds = await getWorkflowyCredentials()
    if (!creds) {
      console.warn('[WorkflowyAPI] No credentials configured, skipping update')
      return false
    }

    const nodeId = taskId.replace('wf-', '')
    const endpoint = completed
      ? `/nodes/${nodeId}/complete`
      : `/nodes/${nodeId}/uncomplete`

    await workflowyApiRequest(endpoint, { method: 'POST' })
    console.log('[WorkflowyAPI] Task completion toggled:', taskId, completed)
    return true
  } catch (error) {
    console.error('[WorkflowyAPI] Failed to toggle completion:', error)
    return false
  }
}

/**
 * Update task date in Workflowy
 * Removes existing date hashtags and adds new one
 */
export async function updateWorkflowyTaskDate(taskId: string, newDate: string | null): Promise<boolean> {
  try {
    const creds = await getWorkflowyCredentials()
    if (!creds) {
      console.warn('[WorkflowyAPI] No credentials configured, skipping update')
      return false
    }

    const nodeId = taskId.replace('wf-', '')
    const node = await getNode(nodeId)
    const currentName = node.name || ''

    // Remove existing dates (hashtag format and natural format)
    let newName = currentName
      .replace(/#?\d{4}-\d{2}-\d{2}/g, '')
      .replace(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2},?\s*\d{4}/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim()

    // Add new date in hashtag format (Workflowy recognizes #YYYY-MM-DD)
    if (newDate) {
      newName = `${newName} #${newDate}`
    }

    await updateNode(nodeId, { name: newName.trim() })
    console.log('[WorkflowyAPI] Task date updated:', taskId, newDate)
    return true
  } catch (error) {
    console.error('[WorkflowyAPI] Failed to update date:', error)
    return false
  }
}

/**
 * Update task time in Workflowy
 * Time format in Workflowy: HH.MM at the beginning
 */
export async function updateWorkflowyTaskTime(taskId: string, newTime: string | null): Promise<boolean> {
  try {
    const creds = await getWorkflowyCredentials()
    if (!creds) {
      console.warn('[WorkflowyAPI] No credentials configured, skipping update')
      return false
    }

    const nodeId = taskId.replace('wf-', '')
    const node = await getNode(nodeId)
    const currentName = node.name || ''

    // Remove existing time at beginning (HH.MM or HH:MM format)
    let newName = currentName.replace(/^\d{1,2}[.:]\d{2}\s+/, '')

    // Add new time at beginning (convert to Workflowy format: HH.MM)
    if (newTime) {
      const formattedTime = newTime.replace(':', '.')
      newName = `${formattedTime} ${newName}`
    }

    await updateNode(nodeId, { name: newName.trim() })
    console.log('[WorkflowyAPI] Task time updated:', taskId, newTime)
    return true
  } catch (error) {
    console.error('[WorkflowyAPI] Failed to update time:', error)
    return false
  }
}

/**
 * Update task name in Workflowy
 * Preserves time, date, duration, and other metadata
 */
export async function updateWorkflowyTaskName(taskId: string, newName: string): Promise<boolean> {
  try {
    const creds = await getWorkflowyCredentials()
    if (!creds) {
      console.warn('[WorkflowyAPI] No credentials configured, skipping update')
      return false
    }

    const nodeId = taskId.replace('wf-', '')
    const node = await getNode(nodeId)
    const currentName = node.name || ''

    // Extract metadata from current name
    const timeMatch = currentName.match(/^\d{1,2}\.\d{2}/)
    const durationMatch = currentName.match(/\b\d+\s*h(?:\s*\d+\s*m)?\b|\b\d+\s*m\b/i)
    const dateMatch = currentName.match(/#\d{4}-\d{2}-\d{2}/)
    const hashtagsMatch = currentName.match(/#\w+/g)
    const hashtags = hashtagsMatch ? hashtagsMatch.filter((tag: string) => !tag.match(/#\d{4}-\d{2}-\d{2}/)) : []

    // Build new name with preserved metadata
    let rebuiltName = newName

    // Add time at beginning
    if (timeMatch) {
      rebuiltName = `${timeMatch[0]} ${rebuiltName}`
    }

    // Add duration after time or at beginning
    if (durationMatch) {
      if (timeMatch) {
        rebuiltName = rebuiltName.replace(/^(\d{1,2}\.\d{2})\s*/, `$1 ${durationMatch[0]} `)
      } else {
        rebuiltName = `${durationMatch[0]} ${rebuiltName}`
      }
    }

    // Add date hashtag at end
    if (dateMatch) {
      rebuiltName = `${rebuiltName} ${dateMatch[0]}`
    }

    // Add other hashtags at end
    if (hashtags.length > 0) {
      rebuiltName = `${rebuiltName} ${hashtags.join(' ')}`
    }

    await updateNode(nodeId, { name: rebuiltName.trim() })
    console.log('[WorkflowyAPI] Task name updated:', taskId)
    return true
  } catch (error) {
    console.error('[WorkflowyAPI] Failed to update name:', error)
    return false
  }
}

/**
 * Check if Workflowy API is available
 */
export async function isWorkflowyApiAvailable(): Promise<boolean> {
  const creds = await getWorkflowyCredentials()
  return !!(creds?.apiKey)
}

/**
 * Fetch all nodes from Workflowy
 */
async function fetchAllNodes(): Promise<any[]> {
  try {
    const response = await workflowyApiRequest('/nodes-export')
    return response || []
  } catch (error) {
    console.error('[WorkflowyAPI] Failed to fetch nodes:', error)
    return []
  }
}

/**
 * Parse due date from Workflowy node name
 * Workflowy uses format: [YYYY-MM-DD] or [YYYY-MM-DD HH:MM]
 * Returns ISO date string: YYYY-MM-DD
 */
function parseWorkflowyDate(text: string): string | null {
  // Match [YYYY-MM-DD] or [YYYY-MM-DD HH:MM]
  const datePattern = /\[(\d{4}-\d{2}-\d{2})(?:\s+\d{2}:\d{2})?\]/
  const match = text.match(datePattern)
  return match ? match[1] : null
}

/**
 * Parse time from Workflowy node name
 * Format: [YYYY-MM-DD HH:MM] or standalone HH.MM at beginning
 */
function parseWorkflowyTime(text: string): string | null {
  // Match time in bracket format [YYYY-MM-DD HH:MM]
  const bracketTimeMatch = text.match(/\[\d{4}-\d{2}-\d{2}\s+(\d{2}):(\d{2})\]/)
  if (bracketTimeMatch) {
    return `${bracketTimeMatch[1]}:${bracketTimeMatch[2]}`
  }

  // Match standalone time at beginning (HH.MM format)
  const standaloneTimeMatch = text.match(/^(\d{1,2})\.(\d{2})/)
  if (standaloneTimeMatch) {
    const hours = standaloneTimeMatch[1].padStart(2, '0')
    const minutes = standaloneTimeMatch[2]
    return `${hours}:${minutes}`
  }

  return null
}

/**
 * Parse duration from Workflowy node name
 * Formats: 2h, 30min, 2h30m, etc.
 */
function parseWorkflowyDuration(text: string): number | null {
  let totalMinutes = 0

  // Match hours
  const hoursMatch = text.match(/(\d+)\s*h(?:ours?)?/i)
  if (hoursMatch) {
    totalMinutes += parseInt(hoursMatch[1]) * 60
  }

  // Match minutes
  const minutesMatch = text.match(/(\d+)\s*m(?:in)?(?:utes?)?/i)
  if (minutesMatch) {
    totalMinutes += parseInt(minutesMatch[1])
  }

  return totalMinutes > 0 ? totalMinutes : null
}

/**
 * Check if node is a task (has date, time, or task-like content)
 */
function isTaskNode(node: any): boolean {
  const name = node.name || ''

  // Has date
  if (parseWorkflowyDate(name)) return true

  // Has time
  if (parseWorkflowyTime(name)) return true

  // Has duration
  if (parseWorkflowyDuration(name)) return true

  // Has task-like keywords
  if (name.match(/\b(todo|task|deadline|due|meeting)\b/i)) return true

  return false
}

/**
 * Clean task name by removing metadata
 */
function cleanWorkflowyTaskName(text: string): string {
  let cleaned = text
    // Remove dates [YYYY-MM-DD] or [YYYY-MM-DD HH:MM]
    .replace(/\[\d{4}-\d{2}-\d{2}(?:\s+\d{2}:\d{2})?\]/g, '')
    // Remove time at beginning (HH.MM format)
    .replace(/^\d{1,2}\.\d{2}\s+/, '')
    // Remove duration
    .replace(/\b\d+\s*h(?:\s*\d+\s*m(?:in)?)?\b/gi, '')
    .replace(/\b\d+\s*m(?:in)?\b/gi, '')
    // Remove hashtag dates
    .replace(/#\d{4}-\d{2}-\d{2}/g, '')
    // Clean up whitespace
    .replace(/\s+/g, ' ')
    .trim()

  return cleaned || 'Untitled Task'
}

/**
 * Export nodes from Workflowy
 */
export async function exportWorkflowyNodes(): Promise<any[]> {
  const nodes = await fetchAllNodes()

  const tasks = nodes
    .filter(isTaskNode)
    .map(node => {
      const name = node.name || ''
      const dueDate = parseWorkflowyDate(name)
      const dueTime = parseWorkflowyTime(name)
      const duration = parseWorkflowyDuration(name)

      return {
        id: `wf-${node.id}`,
        name: cleanWorkflowyTaskName(name),
        note: node.note || '',
        dueDate,
        dueTime,
        duration,
        completed: node.completed || false,
        priority: node.priority || 0,
        tags: ['workflowy'],
        source: 'workflowy',
        _nodeId: node.id,
        _originalName: name,
        _parentId: node.parent_id
      }
    })

  return tasks
}
